import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
    FileText, Download, CheckCircle, AlertTriangle, Home, Euro,
    Calendar, MapPin, User, MessageSquare, Eye, Loader2, X
} from 'lucide-react';

function HomlyLogo() {
    return (
        <div className="font-black text-2xl flex items-center gap-0.5">
            <span className="bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent">Homly</span>
            <span className="text-[#FF914D]">.</span>
        </div>
    );
}

export default function MandateConsultationPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    
    const [mandate, setMandate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);
    const [showPDF, setShowPDF] = useState(false);
    const [comment, setComment] = useState('');
    
    useEffect(() => {
        if (token) {
            loadMandate();
        } else {
            setLoading(false);
        }
    }, [token]);
    
    const loadMandate = async () => {
        try {
            const res = await api.get(`/mandates-v2/consultation/${token}`);
            setMandate(res.data);
            setAcknowledged(res.data.acknowledged);
        } catch (err) {
            console.error('Error loading mandate:', err);
            toast.error('Mandat non trouvé ou lien expiré');
        } finally {
            setLoading(false);
        }
    };
    
    const handleAcknowledge = async () => {
        setSubmitting(true);
        try {
            await api.post(`/mandates-v2/acknowledge/${token}`, null, {
                params: { comment: comment || undefined }
            });
            // Update state first, then show toast with delay to avoid DOM conflicts
            setAcknowledged(true);
            setTimeout(() => {
                toast.success('Mandat validé avec succès');
            }, 100);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la validation');
        } finally {
            setSubmitting(false);
        }
    };
    
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#681793] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    if (!token || !mandate) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <div className="text-center max-w-md">
                    <div className="w-16 h-16 rounded-2xl bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                        <X className="w-8 h-8 text-red-400" />
                    </div>
                    <h2 className="text-xl font-bold text-white mb-2">Lien invalide ou expiré</h2>
                    <p className="text-muted-foreground mb-6">
                        Ce lien de consultation n'est plus valide. Veuillez contacter votre agent immobilier.
                    </p>
                    <Link to="/">
                        <Button variant="outline" className="border-border text-white">
                            Retour à l'accueil
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }
    
    const salePrice = mandate.pricing?.sale_price || 0;
    const commissionRate = mandate.pricing?.commission_rate || 0;
    const commissionAmount = salePrice * commissionRate / 100;
    
    return (
        <div className="min-h-screen bg-background text-white">
            {/* Header */}
            <nav className="bg-background/80 backdrop-blur-xl border-b border-border">
                <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
                    <HomlyLogo />
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Document Bêta
                    </Badge>
                </div>
            </nav>
            
            {/* Main content */}
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Beta warning */}
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-6"
                >
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                        <div>
                            <h4 className="font-semibold text-amber-400">Document Bêta - Non contractuel</h4>
                            <p className="text-sm text-amber-300/70 mt-1">
                                Ce document est généré dans le cadre d'un test bêta Homly. 
                                Il ne remplace pas un mandat signé via une procédure conforme.
                                Le mandat officiel doit être signé en dehors de Homly.
                            </p>
                        </div>
                    </div>
                </motion.div>
                
                {/* Already validated */}
                {acknowledged && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="p-6 rounded-xl bg-green-500/10 border border-green-500/20 mb-6 text-center"
                    >
                        <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                        <h3 className="text-xl font-bold text-green-400 mb-2">
                            Mandat validé
                        </h3>
                        <p className="text-green-300/70">
                            Vous avez validé ce mandat. L'agent immobilier a été notifié.
                        </p>
                    </motion.div>
                )}
                
                {/* Mandate info card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden"
                >
                    {/* Header */}
                    <div className="p-6 border-b border-border bg-gradient-to-r from-[#681793]/10 to-[#FF914D]/10">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Mandat de vente</p>
                                <h2 className="text-2xl font-bold text-white">
                                    N° {mandate.mandate_number}
                                </h2>
                            </div>
                            <Badge className="bg-[#681793]/20 text-[#681793] border-0 text-lg px-3 py-1">
                                v{mandate.current_version}
                            </Badge>
                        </div>
                    </div>
                    
                    {/* Property info */}
                    <div className="p-6 border-b border-border">
                        <h3 className="text-sm font-semibold text-muted-foreground mb-4 flex items-center gap-2">
                            <Home className="w-4 h-4" />
                            Bien immobilier
                        </h3>
                        <div className="flex items-start gap-3">
                            <div className="w-12 h-12 rounded-xl bg-[#681793]/20 flex items-center justify-center">
                                <Home className="w-6 h-6 text-[#681793]" />
                            </div>
                            <div>
                                <p className="font-medium text-white capitalize">
                                    {mandate.property?.property_type?.replace('_', ' ')}
                                </p>
                                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                    <MapPin className="w-3 h-3" />
                                    {mandate.property?.address}, {mandate.property?.postal_code} {mandate.property?.city}
                                </p>
                                <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                                    <span>{mandate.property?.surface_habitable} m²</span>
                                    <span>•</span>
                                    <span>{mandate.property?.rooms} pièces</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Price info */}
                    <div className="p-6 border-b border-border">
                        <h3 className="text-sm font-semibold text-muted-foreground mb-4 flex items-center gap-2">
                            <Euro className="w-4 h-4" />
                            Prix et honoraires
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs text-muted-foreground">Prix de vente</p>
                                <p className="text-2xl font-bold text-white">
                                    {salePrice.toLocaleString('fr-FR')} €
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground">Honoraires ({commissionRate}%)</p>
                                <p className="text-xl font-semibold text-[#FF914D]">
                                    {commissionAmount.toLocaleString('fr-FR')} €
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Duration */}
                    <div className="p-6 border-b border-border">
                        <h3 className="text-sm font-semibold text-muted-foreground mb-4 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            Durée du mandat
                        </h3>
                        <p className="text-foreground">
                            {mandate.duration?.initial_months || 3} mois à compter du{' '}
                            {mandate.duration?.start_date 
                                ? new Date(mandate.duration.start_date).toLocaleDateString('fr-FR')
                                : '-'
                            }
                        </p>
                    </div>
                    
                    {/* Actions */}
                    {!acknowledged && (
                        <div className="p-6">
                            <div className="mb-4">
                                <label className="text-sm text-muted-foreground mb-2 block flex items-center gap-1">
                                    <MessageSquare className="w-4 h-4" />
                                    Commentaire (optionnel)
                                </label>
                                <textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder="Une question, une remarque..."
                                    rows={2}
                                    className="w-full px-4 py-3 bg-card border border-border rounded-xl text-white placeholder:text-white/30 resize-none"
                                />
                            </div>
                            
                            <Button
                                onClick={handleAcknowledge}
                                disabled={submitting}
                                className="w-full h-14 bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-lg font-semibold"
                            >
                                {submitting ? (
                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                ) : (
                                    <CheckCircle className="w-5 h-5 mr-2" />
                                )}
                                Valider le mandat
                            </Button>
                            
                            <p className="text-xs text-white/30 text-center mt-4">
                                En cliquant, vous confirmez avoir consulté et accepté les termes de ce mandat.<br />
                                Une notification sera envoyée à votre agent immobilier.
                            </p>
                        </div>
                    )}
                </motion.div>
                
                {/* Generated date */}
                {mandate.generated_at && (
                    <p className="text-xs text-white/30 text-center mt-6">
                        Document généré le {new Date(mandate.generated_at).toLocaleString('fr-FR')}
                    </p>
                )}
            </div>
            
            {/* Footer */}
            <footer className="border-t border-border mt-12">
                <div className="max-w-4xl mx-auto px-4 py-6 text-center">
                    <HomlyLogo />
                    <p className="text-xs text-white/30 mt-2">
                        © 2026 Homly. Plateforme de gestion immobilière.
                    </p>
                </div>
            </footer>
        </div>
    );
}
